import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import api from "../lib/api";
import { IMAGES, FALLBACK_IMAGE } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import FullImage from "../components/site/FullImage";

export default function Blog() {
  useSEO("Journal", "Articles and cultural updates from ÀsÀNigHT Worldwide — exploring Yorùbá traditions, spirituality, art, and history.");
  const [articles, setArticles] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback((query = "") => {
    setLoading(true);
    api
      .get(`/articles${query ? `?q=${encodeURIComponent(query)}` : ""}`)
      .then((r) => setArticles(r.data))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    load(q);
  };

  return (
    <div>
      <header className="relative bg-forest text-cream grain-overlay">
        <div className="absolute inset-0 opacity-[0.12] bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.adirePattern})` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">The Journal</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl leading-tight"
          >
            Stories, Insights & Updates
          </motion.h1>
          <form onSubmit={onSearch} className="mt-10 max-w-lg mx-auto flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/50" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles..."
                data-testid="blog-search-input"
                className="w-full bg-cream/5 border border-cream/20 text-cream placeholder:text-cream/40 rounded-md pl-11 pr-4 py-3 outline-none focus:border-gold focus:ring-1 focus:ring-gold"
              />
            </div>
            <button type="submit" data-testid="blog-search-button" className="bg-gold text-night font-semibold px-6 rounded-md hover:bg-gold-light transition-colors">
              Search
            </button>
          </form>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-20 min-h-[40vh]">
        {loading ? (
          <p className="text-center text-ink-muted">Loading articles...</p>
        ) : articles.length === 0 ? (
          <p className="text-center text-ink-muted" data-testid="blog-empty-state">No articles found.</p>
        ) : (
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.06}>
                <Link
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group block h-full border border-parchment rounded-xl overflow-hidden bg-cream hover:-translate-y-1 hover:border-gold transition-all duration-300"
                >
                  <FullImage src={a.cover_image || FALLBACK_IMAGE} alt={a.title} className="h-52" imgClassName="group-hover:scale-105 transition-transform duration-700" />
                  <div className="p-7">
                    <p className="text-xs uppercase tracking-widest text-gold mb-3">{a.category}</p>
                    <h2 className="font-serif text-2xl text-ink leading-snug group-hover:text-forest transition-colors mb-3">{a.title}</h2>
                    <p className="text-ink-muted line-clamp-3">{a.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <Newsletter />
    </div>
  );
}
